import notifier from '@albert-io/notifier';
import React, {useEffect} from 'react';
import {Helmet} from 'react-helmet';

import appStore from 'client/AppStore';
import {history} from 'client/history';
import sessionStore from 'client/Session/SessionStore';
import {redirect} from 'client/User/UserRedirectUtil';
import TextInput from 'generic/Forms/TextInput/TextInput';
import {title} from 'lib/head';

import {useResetPasswordStore, useResetPasswordSubmit} from './ResetPassword.store';

import {useShallow} from 'zustand/react/shallow';
import './assets/reset-password.scss';

/**
 * ResetPassword
 *
 * This component handles the password reset flow for users who have
 * received a reset password email link. It validates the email link
 * and allows users to set a new password.
 *
 * Note: This is NOT for logged-in users changing their current password.
 * That flow is handled by the ChangePassword component.
 *
 * @todo [SECURITY] Ensure emailLinkId is NOT logged (Stackdriver, Google Analytics, etc.)
 */
const ResetPassword = () => {
  const {passwordInputValue, isPasswordValid, handlePasswordChange, setParameters, reset} =
    useResetPasswordStore(
      useShallow((state) => ({
        passwordInputValue: state.passwordInputValue,
        isPasswordValid: state.isPasswordValid,
        handlePasswordChange: state.handlePasswordChange,
        setParameters: state.setParameters,
        reset: state.reset
      }))
    );

  const {submitNewPassword: handleSubmit, isSubmitting} = useResetPasswordSubmit();

  // Initialize on mount - equivalent to UNSAFE_componentWillMount
  useEffect(() => {
    // There is a separate flow for logged in users changing their current password.
    if (sessionStore.hasValidSession()) {
      redirect();
      return;
    }

    const {location} = appStore.routerProps as {
      location?: {query?: {userId?: string; emailLinkId?: string}};
    };
    const {userId, emailLinkId} = location?.query ?? {};

    if (!userId || !emailLinkId) {
      logger.error('incomplete parameter set for reset form');

      notifier.notify(new Error('Reset password attempted with incomplete parameters'), {
        tags: 'ResetPassword',
        context: {
          hasUserId: !!userId,
          hasEmailLinkId: !!emailLinkId
        }
      });

      history.replaceState(null, '/');
      return;
    }

    setParameters({userId, emailLinkId});
  }, [setParameters]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      reset();
    };
  }, [reset]);

  const hasError = !!(passwordInputValue && !isPasswordValid);
  const isSubmitDisabled = isSubmitting || !passwordInputValue || !isPasswordValid;
  const metaDescription = 'Set your new password so you can keep learning!';

  return (
    <div className='reset-password-wrapper'>
      <Helmet>{title('Reset Password | Albert')}</Helmet>
      <div className='reset-password-content'>
        <div className='reset-password-meta'>
          <h1 className='reset-password-meta-title'>Reset Password</h1>
          <p className='reset-password-meta-description'>{metaDescription}</p>
        </div>

        <div className='a-form-wrapper'>
          <form className='a-form' onSubmit={handleSubmit}>
            <div className='a-form-row'>
              <TextInput
                name='password'
                label='New Password'
                type='password'
                autoComplete='new-password'
                required
                value={passwordInputValue}
                error={hasError}
                errorMessage='Passwords must have at least 8 characters and at least one number or special character.'
                onFocusValidation
                onChange={handlePasswordChange}
                disabled={isSubmitting}
              />
            </div>
            <div className='a-form-row'>
              <button className='a-form-submit-button' type='submit' disabled={isSubmitDisabled}>
                {isSubmitting ? 'Setting Password...' : 'Set New Password'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
